// SelectRole.jsx — /role?context=property | /role?context=project
// Step 1: Pick role → Step 2 (property: pick listing type) → navigate to create screen

import React, { useState, useContext, useEffect, useTransition, useMemo, memo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { MyContext } from "../../../../Context/MyContext";
import {
  checkUserKycGate,
  navigateToUserKyc,
  prefetchUserKycGates,
} from "../../../../utils/userKyc";
import KycRequiredModal from "../../../../Components/KycRequiredModal";
import staticRoles from "../../../../json/roles";
import staticListingTypes from "../../../../json/listingTypes";
import "./SelectScreens.css";

// ─────────────────────────────────────────────────────────────
// Step 1 — Role
// ─────────────────────────────────────────────────────────────
const StepRole = memo(({ context, roles, onSelect, busyRoleId }) => {
  const totalSteps = context === "property" ? 3 : 2;
  const isProperty = context === "property";
  const options = roles?.length ? roles : staticRoles;

  return (
    <div className="sel-screen">
      <div className="sel-screen-inner">

        <div className="sel-header">
          <div className="sel-step-badge">Step 1 of {totalSteps}</div>
          <h1 className="sel-title">Tell Us About You</h1>
          <p className="sel-subtitle">
            Choose your role to continue with your {" "}
            {isProperty ? "property listing" : "project listing"}.
          </p>
        </div>

        <div className="sel-cards">
          {options.map(opt => {
            const label = isProperty ? opt.property_label : opt.project_label;
            const desc  = isProperty ? opt.property_description : opt.project_description;
            const isBusy = busyRoleId === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                className={`sel-card${isBusy ? " is-selected is-busy" : ""}`}
                disabled={Boolean(busyRoleId)}
                aria-busy={isBusy}
                onClick={() => onSelect({ id: opt.id, label })}
              >
                <span className="sel-card-label">{label}</span>
                <span className="sel-card-desc">
                  {isBusy ? "Checking access…" : desc}
                </span>
                <span className="sel-card-arrow">
                  {isBusy ? (
                    <span className="sel-card-spinner" aria-hidden="true" />
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" width="18" height="18">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  )}
                </span>
              </button>
            );
          })}
        </div>

      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────
// Step 2 — Listing Type (property only)
// ─────────────────────────────────────────────────────────────
const StepListingType = memo(({ selectedRole, listingTypes, onSelect, onBack, busyListingId }) => {
  const options = listingTypes?.length ? listingTypes : staticListingTypes;
  return (
    <div className="sel-screen">
      <div className="sel-screen-inner">

        <button type="button" className="sel-back-btn" onClick={onBack} disabled={Boolean(busyListingId)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" width="16" height="16">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Back
        </button>

        <div className="sel-header">
          <div className="sel-step-badge">Step 2 of 3</div>
          {selectedRole && (
            <div className="sel-context-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" width="13" height="13">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              {selectedRole.label}
            </div>
          )}
          <h1 className="sel-title">Choose Listing Type</h1>
          <p className="sel-subtitle">How would you like to showcase your property?</p>
        </div>

        <div className="sel-cards">
          {options.map(opt => {
            const isBusy = busyListingId === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                className={`sel-card${isBusy ? " is-selected is-busy" : ""}`}
                disabled={Boolean(busyListingId)}
                aria-busy={isBusy}
                onClick={() => onSelect(opt)}
              >
                <span className="sel-card-label">{opt.label}</span>
                <span className="sel-card-desc">
                  {isBusy ? "Opening form…" : opt.description}
                </span>
                <span className="sel-card-arrow">
                  {isBusy ? (
                    <span className="sel-card-spinner" aria-hidden="true" />
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" width="18" height="18">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  )}
                </span>
              </button>
            );
          })}
        </div>

      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────
// Main — orchestrates steps + navigation
// ─────────────────────────────────────────────────────────────
const SelectRole = () => {
  const navigate       = useNavigate();
  const location       = useLocation();
  const [searchParams] = useSearchParams();
  const { roles, listingTypes } = useContext(MyContext);
  const [, startTransition] = useTransition();

  // context comes from URL: /role?context=property  OR  /role?context=project
  // defaults to "property"
  const context = searchParams.get("context") === "project" ? "project" : "property";

  const [step,            setStep]            = useState(1); // 1 = role, 2 = listing type
  const [selectedRole,    setSelectedRole]    = useState(null);
  const [kycModal,        setKycModal]        = useState(null);
  const [busyRoleId,      setBusyRoleId]      = useState(null);
  const [busyListingId,   setBusyListingId]   = useState(null);

  const roleOptions = useMemo(
    () => (roles?.length ? roles : staticRoles),
    [roles],
  );
  const listingTypeOptions = useMemo(
    () => (listingTypes?.length ? listingTypes : staticListingTypes),
    [listingTypes],
  );

  // Prefetch KYC gates so Step 1 clicks usually hit cache
  useEffect(() => {
    prefetchUserKycGates(roleOptions.map((r) => r.id));
  }, [roleOptions]);

  useEffect(() => {
    const resumeRole = location.state?.resumeRole;
    if (!resumeRole) return;
    setSelectedRole({ id: resumeRole, label: resumeRole });
    if (context === "property") setStep(2);
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [context, location.pathname, location.search, location.state?.resumeRole, navigate]);

  // ── Step 1: role chosen ──────────────────────────────────
  const handleRoleSelect = async (role) => {
    if (busyRoleId) return;
    setSelectedRole(role);
    setBusyRoleId(role.id);

    try {
      const gate = await checkUserKycGate(role.id);
      if (!gate.allowed) {
        setKycModal({
          message: gate.message,
          roleLabel: gate.role_label || role.label,
          kycStatus: gate.kyc_status,
          roleId: role.id,
        });
        return;
      }

      if (context === "project") {
        // Project: only 2 steps → go straight to create
        const params = new URLSearchParams({ role: role.id });
        const url    = `/project/create?${params.toString()}`;
        startTransition(() => navigate(url));
      } else {
        // Property: show listing type next
        setStep(2);
      }
    } finally {
      setBusyRoleId(null);
    }
  };

  // ── Step 2: listing type chosen ──────────────────────────
  const handleListingTypeSelect = (listingType) => {
    if (busyListingId || !selectedRole?.id) return;
    setBusyListingId(listingType.id);

    const params = new URLSearchParams({
      role:     selectedRole.id,
      listtype: listingType.id,
    });
    const url = `/property/create?${params.toString()}`;

    // Paint selected/busy state first, then navigate (same destination + params)
    startTransition(() => {
      navigate(url);
    });
  };

  const handleGoToKyc = () => {
    if (!kycModal) return;
    navigateToUserKyc(navigate, {
      role: kycModal.roleId,
      pendingFlow: {
        context,
        role: kycModal.roleId,
        blockedAt: 'role_select',
      },
    });
    setKycModal(null);
  };

  // ── Render ───────────────────────────────────────────────
  const screen = step === 1 ? (
    <StepRole
      context={context}
      roles={roleOptions}
      onSelect={handleRoleSelect}
      busyRoleId={busyRoleId}
    />
  ) : (
    <StepListingType
      selectedRole={selectedRole}
      listingTypes={listingTypeOptions}
      onSelect={handleListingTypeSelect}
      onBack={() => { if (!busyListingId) setStep(1); }}
      busyListingId={busyListingId}
    />
  );

  return (
    <>
      {screen}
      <KycRequiredModal
        open={Boolean(kycModal)}
        message={kycModal?.message}
        roleLabel={kycModal?.roleLabel}
        kycStatus={kycModal?.kycStatus}
        onClose={() => setKycModal(null)}
        onGoToKyc={handleGoToKyc}
      />
    </>
  );
};

export default SelectRole;
